import {
  CodeBlock,
  CodeBlockCode,
} from "@arcadeai/design-system/components/ui/atoms/code-block";
import { CopyButton } from "@arcadeai/design-system/components/ui/atoms/copy-button";
import {
  Claude,
  Cline,
  Cursor,
  Goose,
  Openai,
  Vscode,
  Windsurf,
} from "@arcadeai/design-system/components/ui/atoms/icons";
import { MCP_CLIENT_METADATA } from "@arcadeai/design-system/metadata/mcp-clients";
import { Link2 } from "lucide-react";
import type { ReactNode } from "react";
import type {
  ConfigFooterContext,
  McpAuthType,
  McpClientConfig,
  McpClientId,
} from "./types";
import {
  buildClaudeCodeCommand,
  buildCodexToml,
  buildCursorDeepLink,
  buildGatewayUrl,
  buildGooseYaml,
  buildMcpServersJson,
  buildVscodeDeepLink,
  buildVscodeJson,
  createSafeKey,
} from "./utils";

/**
 * Footnote rendered by clients whose generated config contains
 * `<your-api-key>` / `<user-identifier>` placeholders the user must replace.
 */
function ApiKeyPlaceholderHint({ ctx }: { ctx: ConfigFooterContext }) {
  if (ctx.authType !== "arcade_header") {
    return null;
  }
  return (
    <p className="text-muted-foreground text-xs">
      Replace <code className="text-foreground">&lt;your-api-key&gt;</code> with
      your Arcade API key
      {!ctx.userId && (
        <>
          {" "}
          and <code className="text-foreground">&lt;user-identifier&gt;</code>{" "}
          with a unique user identifier, like an email address
        </>
      )}
      .
    </p>
  );
}

/**
 * Registry of supported MCP clients with their configurations.
 * To add a new client, add an entry here — no other files need to change.
 * The McpClientId type is inferred from the keys of this object.
 */
export const MCP_CLIENTS = {
  cursor: {
    name: MCP_CLIENT_METADATA.cursor.label,
    supportsDeepLink: true,
    supportedAuthTypes: ["arcade", "arcade_header", "user_source"],
    logo: <Cursor className="text-foreground" />,
    format: "json",
    buildConfig: (ctx) =>
      buildMcpServersJson(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    buildDeepLink: (ctx) =>
      buildCursorDeepLink(ctx.enginePublicUrl, ctx.gatewayName, ctx.slug),
    setupHint: "Add this snippet to your Cursor settings",
    renderFooter: (ctx) => <ApiKeyPlaceholderHint ctx={ctx} />,
  },
  vscode: {
    name: MCP_CLIENT_METADATA.vscode.label,
    supportsDeepLink: true,
    supportedAuthTypes: ["arcade", "arcade_header", "user_source"],
    logo: <Vscode />,
    format: "json",
    buildConfig: (ctx) =>
      buildVscodeJson(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    buildDeepLink: (ctx) =>
      buildVscodeDeepLink(ctx.enginePublicUrl, ctx.gatewayName, ctx.slug),
    setupHint: "Add this snippet to your VS Code settings",
    renderFooter: (ctx) => <ApiKeyPlaceholderHint ctx={ctx} />,
  },
  goose: {
    name: MCP_CLIENT_METADATA.goose.label,
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade_header"],
    logo: <Goose className="text-foreground" />,
    format: "yaml",
    buildConfig: (ctx) =>
      buildGooseYaml(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    setupHint: "Add this snippet to your Goose config.yaml",
    renderFooter: (ctx) => <ApiKeyPlaceholderHint ctx={ctx} />,
  },
  claude_desktop: {
    name: MCP_CLIENT_METADATA.claude_desktop.label,
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade", "user_source"],
    logo: <Claude />,
    format: "text",
    buildConfig: (ctx) => buildGatewayUrl(ctx.enginePublicUrl, ctx.slug),
    setupHint:
      "Go to Settings → Connectors → Add Custom Connector and paste the URL below",
    renderFooter: (ctx) => <ApiKeyPlaceholderHint ctx={ctx} />,
  },
  claude_code: {
    name: MCP_CLIENT_METADATA.claude_code.label,
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade", "arcade_header", "user_source"],
    logo: <Claude />,
    format: "command",
    buildConfig: (ctx) =>
      buildClaudeCodeCommand(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    setupHint: "Run in terminal",
    renderFooter: (ctx) => (
      <>
        <ApiKeyPlaceholderHint ctx={ctx} />
        <p className="text-muted-foreground text-xs">
          This command installs for the current project. Add{" "}
          <code className="text-foreground">--scope user</code> for a user-wide
          install.
        </p>
      </>
    ),
  },
  windsurf: {
    name: MCP_CLIENT_METADATA.windsurf.label,
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade", "arcade_header", "user_source"],
    logo: <Windsurf className="text-foreground" />,
    format: "json",
    buildConfig: (ctx) =>
      buildMcpServersJson(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    setupHint: "Add this snippet to your Windsurf mcp_config.json",
    renderFooter: (ctx) => <ApiKeyPlaceholderHint ctx={ctx} />,
  },
  cline: {
    name: MCP_CLIENT_METADATA.cline.label,
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade_header"],
    logo: <Cline className="text-foreground" />,
    format: "json",
    buildConfig: (ctx) =>
      buildMcpServersJson(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    setupHint: "Add this snippet to your Cline MCP settings",
    renderFooter: (ctx) => <ApiKeyPlaceholderHint ctx={ctx} />,
  },
  codex: {
    name: MCP_CLIENT_METADATA.codex.label,
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade", "arcade_header", "user_source"],
    logo: <Openai className="text-foreground" />,
    format: "toml",
    buildConfig: (ctx) =>
      buildCodexToml(
        ctx.enginePublicUrl,
        ctx.gatewayName,
        ctx.slug,
        ctx.authType,
        ctx.userId
      ),
    setupHint: "Add this snippet to your Codex config.toml",
    renderFooter: (ctx) => {
      if (ctx.authType === "arcade_header") {
        return <ApiKeyPlaceholderHint ctx={ctx} />;
      }
      const loginCommand = `codex mcp login ${createSafeKey(ctx.gatewayName, "_")}`;
      return (
        <>
          <div className="flex items-center justify-between pt-2">
            <span className="text-muted-foreground text-sm">
              Then run this in your terminal to authenticate
            </span>
            <CopyButton
              content={loginCommand}
              onCopy={() => ctx.onConfigCopy?.("command")}
              variant="ghost"
            />
          </div>
          <CodeBlock>
            <CodeBlockCode
              className="[&_.shiki]:whitespace-pre-wrap [&_.shiki]:break-all"
              code={loginCommand}
              language="bash"
            />
          </CodeBlock>
        </>
      );
    },
  },
  chatgpt: {
    name: "ChatGPT",
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade", "user_source"],
    logo: <Openai className="text-foreground" />,
    format: "text",
    buildConfig: (ctx) => buildGatewayUrl(ctx.enginePublicUrl, ctx.slug),
    setupHint: "Your MCP server endpoint — paste this into ChatGPT",
    renderFooter: () => (
      <div className="space-y-2">
        <ol className="list-inside list-decimal space-y-1 text-muted-foreground text-xs">
          <li>Enable Developer mode for your workspace / account.</li>
          <li>
            Go to{" "}
            <strong className="text-foreground">
              Settings → Apps → Create
            </strong>
            .
          </li>
          <li>Enter the MCP server endpoint above.</li>
          <li>
            For authentication, choose{" "}
            <strong className="text-foreground">OAuth</strong>.
          </li>
          <li>Complete OAuth authorization.</li>
          <li>
            Click <strong className="text-foreground">Create</strong>.
          </li>
        </ol>
        <p className="text-muted-foreground text-xs">
          Requires a ChatGPT Business, Enterprise, or Edu plan. Plus and Pro are
          read-only.
        </p>
      </div>
    ),
  },
  mcp_url: {
    name: "MCP URL",
    supportsDeepLink: false,
    supportedAuthTypes: ["arcade", "arcade_header", "user_source"],
    logo: <Link2 className="size-4 text-foreground" />,
    format: "url",
    buildConfig: (ctx) => buildGatewayUrl(ctx.enginePublicUrl, ctx.slug),
    setupHint: "Use with any MCP-compatible client",
    renderFooter: (ctx) => {
      if (ctx.authType !== "arcade_header") {
        return (
          <p className="text-muted-foreground text-xs">
            Connect this URL to any MCP-compatible client.
          </p>
        );
      }
      const headerLines = [
        "Authorization: Bearer <your-api-key>",
        `Arcade-User-ID: ${ctx.userId ?? "<user-identifier>"}`,
      ].join("\n");
      return (
        <>
          <div className="flex items-center justify-between pt-2">
            <span className="text-muted-foreground text-sm">
              Send these headers with every request
            </span>
            <CopyButton
              content={headerLines}
              onCopy={() => ctx.onConfigCopy?.("text")}
              variant="ghost"
            />
          </div>
          <CodeBlock>
            <CodeBlockCode
              className="[&_.shiki]:whitespace-pre-wrap [&_.shiki]:break-all"
              code={headerLines}
              language="http"
            />
          </CodeBlock>
          <p className="text-muted-foreground text-xs">
            Replace{" "}
            <code className="text-foreground">&lt;your-api-key&gt;</code> with
            your Arcade API key
            {!ctx.userId && (
              <>
                {" "}
                and{" "}
                <code className="text-foreground">&lt;user-identifier&gt;</code>{" "}
                with a unique user identifier (like an email address)
              </>
            )}
            .
            {ctx.apiKeysHref && (
              <>
                {" "}
                <a
                  className="text-foreground underline underline-offset-2 hover:no-underline"
                  href={ctx.apiKeysHref}
                >
                  Manage API keys
                </a>
              </>
            )}
          </p>
        </>
      );
    },
  },
} satisfies Record<string, McpClientConfig>;

export function isMcpClientId(value: string | undefined): value is McpClientId {
  return value !== undefined && Object.hasOwn(MCP_CLIENTS, value);
}

export function getClientIdsForAuthType(authType: McpAuthType): McpClientId[] {
  // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
  return (Object.keys(MCP_CLIENTS) as McpClientId[]).filter((id) =>
    // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
    (MCP_CLIENTS[id].supportedAuthTypes as McpAuthType[]).includes(authType)
  );
}

export function getMcpClientMeta(clientId: McpClientId): {
  name: string;
  logo: ReactNode;
} {
  const client = MCP_CLIENTS[clientId];
  return { name: client.name, logo: client.logo };
}

export function canUseDeepLink(
  clientId: McpClientId,
  authType: McpAuthType
): boolean {
  if (authType === "arcade_header") {
    return false;
  }
  return MCP_CLIENTS[clientId].supportsDeepLink;
}
